import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from '@material-ui/core/Modal';
import { isDesktop } from 'react-device-detect';
import Loading from 'react-simple-loading';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import UserStore from 'stores/UserStore';
import { CONTEXTUAL_GOLD_MODAL_AREAS } from 'utils/consts';
import Button from 'components/Button/Button';

import styles from './index.css';


const wrapperClasses = {
  mobile: {
    iframe: styles.mobileIframeWrapper,
    image: styles.mobileImageWrapper,
    video: styles.mobileVideoWrapper,
  },
  desktop: {
    iframe: styles.desktopIframeWrapper,
    image: styles.desktopImageWrapper,
    video: styles.desktopVideoWrapper,
  },
};

export default class ShaftTrafficAdsModal extends Component {
  static propTypes = {
    close: PropTypes.func,
    loading: PropTypes.bool,
    redirectUrl: PropTypes.string,
    settings: PropTypes.object,
    show: PropTypes.bool,
    onLoad: PropTypes.func,
  };

  static defaultProps = {
    close: undefined,
    loading: false,
    redirectUrl: undefined,
    settings: {},
    show: false,
    onLoad: undefined,
  };

  state = { isAdLoaded: false };

  iframeState = { loadEvent: null, hasLoadMessage: false };

  componentDidMount() {
    window.addEventListener('message', this.onInnerIframeLoad, false);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onInnerIframeLoad);
  }

  setIframeRef = (node) => this.iframeNode = node;

  onRemoveAds = (event) => {
    const { user: { username } } = UserStore;

    this.onClose(event, false);

    if (!username) {
      ModalService.showModal(ModalService.MODAL_KEYS.REGISTRATION_GOLD_MODAL, {
        onClose: ModalService.hideModal,
        showRegistrationGoldModal: true,
      });

      return;
    }

    ModalService.showModal(
      ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
      { area: CONTEXTUAL_GOLD_MODAL_AREAS.ADS },
    );
  };

  onClose = (event, shouldRedirect = true) => {
    const { close, loading, redirectUrl } = this.props;
    const { isAdLoaded } = this.state;

    if (loading || !isAdLoaded) {
      return;
    }

    const url = shouldRedirect ? redirectUrl : '';
    close?.(event, url);
  };

  onLoad = (event) => {
    if (this.state.isAdLoaded) {
      return;
    }

    this.props.onLoad?.(event);

    if (!event.defaultPrevented) {
      this.setState({ isAdLoaded: true });
    }
  };

  onIframesLoad = () => {
    const { loadEvent, hasLoadMessage } = this.iframeState;
    if (!loadEvent || !hasLoadMessage) {
      return;
    }

    this.onLoad(loadEvent);
    this.iframeState = { loadEvent: null, hasLoadMessage: false };
  };

  onInnerIframeLoad = (event) => {
    const isValidSource = event.source === this.iframeNode?.contentWindow;
    const isLoaded = event.data?.loaded;

    if (!isValidSource || !isLoaded || this.state.isAdLoaded) {
      return;
    }

    window.removeEventListener('message', this.onInnerIframeLoad);

    this.iframeState.hasLoadMessage = true;
    this.onIframesLoad();
  };

  onIframeLoad = (event) => {
    if (!this.iframeNode || this.state.isAdLoaded) {
      return;
    }

    event.persist();
    this.iframeState.loadEvent = event;
    this.onIframesLoad();
  };

  renderTracker() {
    const { tracker } = this.props.settings;
    if (!tracker) {
      return null;
    }

    return <img alt="" className={styles.tracker} src={tracker} />;
  }

  renderImage() {
    const { type, content } = this.props.settings;
    if (type !== 'image') {
      return null;
    }

    return (
      <img
        key={content}
        alt=""
        className={styles.adImg}
        src={content}
        onLoad={this.onLoad}
      />
    );
  }

  renderIframe() {
    const { type, content } = this.props.settings;
    if (type !== 'iframe') {
      return null;
    }

    return (
      <iframe
        key={content}
        ref={this.setIframeRef}
        className={styles.iframeAd}
        sandbox="allow-popups allow-scripts allow-same-origin allow-forms"
        scrolling="no"
        src={content}
        title="Ads"
        onLoad={this.onIframeLoad}
      />
    );
  }

  renderVideo() {
    const { type, content } = this.props.settings;
    if (type !== 'video') {
      return null;
    }

    return (
      <video
        key={content}
        autoPlay={true}
        className={styles.videoAd}
        loop={true}
        muted={true}
        src={content}
        onCanPlayThrough={this.onLoad}
      />
    );
  }

  renderLoading = () => {
    const { isAdLoaded } = this.state;
    if (isAdLoaded) {
      return null;
    }

    return (
      <div className={styles.loadingAd}>
        <Loading />
      </div>
    );
  };

  renderContent = () => {
    const { loading } = this.props;
    if (loading) {
      return <Loading />;
    }

    const { isAdLoaded } = this.state;
    const { settings } = this.props;
    const { type, click: url } = settings;

    const view = isDesktop ? 'desktop' : 'mobile';

    return (
      <div className={wrapperClasses?.[view]?.[type]}>
        <span
          className={styles.close}
          data-disabled={!isAdLoaded}
          data-functionality="modal-close"
          onClick={this.onClose}
        >
          &times;
        </span>

        <div className={styles.wrapper}>
          <a
            className={styles.wrappingLink}
            href={url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {this.renderTracker()}
            {this.renderImage()}
            {this.renderIframe()}
            {this.renderVideo()}
          </a>
          {this.renderLoading()}
        </div>

        <div className={styles.footer}>
          <Button
            className={styles.removeAdsButton}
            colour="link"
            disabled={!isAdLoaded}
            onClick={this.onRemoveAds}
          >
            {translate('menuoptions.Remove.Ads')}
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const { show } = this.props;

    return (
      <Modal keepMounted={show} open={show} onClose={this.onClose}>
        {this.renderContent()}
      </Modal>
    );
  }
}
